import {
  ApolloClient,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { Reference, relayStylePagination } from "@apollo/client/utilities";
import { RelayFieldPolicy } from "@apollo/client/utilities/policies/pagination";
import { useAuth } from "@src/contexts/AuthProvider";
import { notifyError } from "@src/shared/notify";
import { Page } from "@src/shared/pages";
import { Query } from "@src/__generated__/graphql";
import React, { ReactNode } from "react";

const httpLink = new HttpLink({
  uri: "/graphql",
});

const AuthorizedApolloProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const { removeToken } = useAuth();

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        notifyError(message);
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
      });
    }

    if (networkError) {
      // https://github.com/apollographql/apollo-link/issues/300
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const n = networkError as any;
      const responseCode = n.response.status;
      if (responseCode === 401 && !location.href.startsWith(Page.LOGIN)) {
        removeToken();
        location.href = Page.LOGIN;
      }
    }
  });

  const paginatedFields: PaginatedQueries = {
    vendors: relayStylePagination(),
    spendRequests: relayStylePagination(),
    transactions: relayStylePagination([
      "type",
      "startYearMonth",
      "endYearMonth",
    ]),
    documents: relayStylePagination(),
    documentsWithNoAssociations: relayStylePagination(),
    erpVendorMappings: relayStylePagination(),
    fiGroupingTransactions: relayStylePagination(),
    erpVendors: relayStylePagination(),
    planVendors: relayStylePagination(),
    bills: relayStylePagination(),
  };

  const client = new ApolloClient({
    connectToDevTools: process.env.NODE_ENV !== "production",
    link: from([errorLink, httpLink]),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "network-only",
      },
    },
    cache: new InMemoryCache({
      typePolicies: {
        ApplicationConfigurationObject: {
          keyFields: [],
        },
        Query: {
          fields: paginatedFields,
        },
        FiGroupingSpendRequestObject: {
          keyFields: ["id", "lineId"],
        },
      },
    }),
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

type PaginatedQueries = {
  [K in keyof Omit<Query, "__typename">]?: RelayFieldPolicy<Reference>;
};

export default AuthorizedApolloProvider;
